import { useEffect } from "react";
import { motion } from "framer-motion";
import { Ruler, Key, Building2, Handshake } from "lucide-react";
import Marquee from "@/components/Marquee";
import Reveal, { MaskedLine } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";

const valuePoints = [
  { icon: Ruler, title: "Pre-lease walkthroughs", copy: "Bring us to the showing. We walk the space and price the work before your client signs." },
  { icon: Key, title: "TI & upfit pricing, fast", copy: "Numbers you can hand to a tenant or landlord while the deal is still warm." },
  { icon: Building2, title: "Any size, one call", copy: "From moving a few walls to a ground-up pad site. No project too large or small." },
  { icon: Handshake, title: "We make you look good", copy: "Straight numbers and straight answers — your client remembers who sent us." },
];

export default function ForBrokers() {
  useEffect(() => {
    document.title = "For Commercial Real Estate Brokers | Rogers American Construction, Charleston SC";
  }, []);

  return (
    <main>
      <section className="relative min-h-[85vh] flex flex-col justify-end overflow-hidden bg-ink text-paper" data-testid="brokers-hero">
        <div className="relative z-10 w-full px-6 md:px-12 pt-40 pb-16">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="text-xs md:text-sm uppercase tracking-[0.25em] text-paper/70 mb-8"
            data-testid="brokers-eyebrow"
          >
            For Commercial Real Estate Brokers — Charleston, SC
          </motion.p>
          <h1 className="font-display font-medium uppercase leading-[0.92] tracking-tighter text-[12vw] md:text-[7.5vw]" data-testid="brokers-headline">
            <MaskedLine delay={0.15}>Get the space</MaskedLine>
            <MaskedLine delay={0.35}>
              <span className="text-accent">lease-ready.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-10 max-w-2xl text-paper/75 text-base md:text-lg leading-relaxed"
            data-testid="brokers-subcopy"
          >
            Rogers American Construction works with Charleston's commercial brokers to walk,
            price, upfit, and renovate spaces — so your listings show well and your deals move.
            25+ years building in Charleston. No project too large or small.
          </motion.p>
        </div>
      </section>

      <Marquee items={["Rogers American Construction", "Building Charleston for 25+ years the right way"]} />

      <section className="px-6 md:px-12 py-20 md:py-28" data-testid="brokers-value-section">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-4">Why brokers call us</p>
          <h2 className="font-display font-medium uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95] mb-14">
            A contractor that helps<br />you close
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-ink/10">
          {valuePoints.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.06}>
              <div className="border-b border-r border-ink/10 p-8 md:p-10 min-h-[220px] flex flex-col justify-between" data-testid={`brokers-value-${i}`}>
                <v.icon className="w-7 h-7 text-accent" strokeWidth={1.5} />
                <div className="mt-10">
                  <h3 className="font-display font-medium uppercase tracking-tight text-xl">{v.title}</h3>
                  <p className="mt-3 text-sm text-smoke leading-relaxed">{v.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-ink/10 px-6 md:px-12 py-20 md:py-28" data-testid="brokers-form-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-6">Next step</p>
              <h2 className="font-display font-medium uppercase leading-[0.92] tracking-tighter text-4xl md:text-5xl" data-testid="brokers-form-title">
                <MaskedLine delay={0.1}>Price a space</MaskedLine>
                <MaskedLine delay={0.26}>for your client.</MaskedLine>
              </h2>
              <p className="mt-8 text-smoke text-base md:text-lg leading-relaxed max-w-md">
                Tell us about the listing or the deal. A person — not an autoresponder —
                reviews every request within one business day.
              </p>
              <div className="mt-10 border-t border-ink/10 pt-8 space-y-4 text-sm text-smoke">
                <p><span className="text-ink font-semibold">Based in Charleston.</span> Serving all of South Carolina.</p>
                <p><span className="text-ink font-semibold">Licensed &amp; insured.</span> Family owned and operated.</p>
                <p><span className="text-ink font-semibold">Prefer to talk?</span> (843) 296-7438</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="md:col-span-7">
            <div className="border border-ink/15 p-8 md:p-12 bg-white">
              <LeadForm mode="quote" presetProjectType="commercial-upfit" />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
